import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {
    Avatar,
    Box,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Popover,
    Typography,
    makeStyles
} from "@material-ui/core";
import SubtractCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/remove-add/subtract-circle.svg";
import AddCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/remove-add/add-circle.svg";
import {CustomButton, CustomIconButton, StreamlineIcon} from "@hapinest/theme";
import SingleMan from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/users/geomertic-close-up-single-user-man/single-man.svg";
import {Skeleton} from "@material-ui/lab";
import InformationCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/alerts/information-circle.svg";
import {useI18N} from "../../../../common/common";


const useStyles = makeStyles((theme) => ({
    root: {
        height: `${theme.spacing(9)}px`,
        border: "1px solid " + theme.palette.borders.line.light,
        borderRadius: 6,
        backgroundColor: theme.palette.background.white,
        paddingBottom: "0px",
        paddingTop: "0px",
        alignItems: "center"
    },
    active: {
        "&:hover": {
            backgroundColor: theme.palette.primary[50]
        },
        "&:hover i": {
            color: theme.palette.primary[900]
        }
    },
    listItemGuest: {
        "&.Mui-disabled .MuiIconButton-root:hover": {
            backgroundColor: "unset",
            cursor: "unset"
        },
        [`&:not(.Mui-disabled) .MuiListItemAvatar-root:hover,
          &:not(.Mui-disabled):has(.MuiListItemText-root > div > div:hover) .MuiListItemAvatar-root`]: {
            borderTopLeftRadius: theme.spacing(4),
            borderBottomLeftRadius: theme.spacing(4)
        },
        [`&:not(.Mui-disabled) .MuiListItemAvatar-root:hover + .MuiListItemText-root > div > div,
          &:not(.Mui-disabled) .MuiListItemText-root > div > div:hover`]: {
            borderTopRightRadius: theme.spacing(4),
            borderBottomRightRadius: theme.spacing(4)
        },
        [`&:not(.Mui-disabled) .MuiListItemAvatar-root:hover,
          &:not(.Mui-disabled):has(.MuiListItemText-root > div > div:hover) .MuiListItemAvatar-root,
          &:not(.Mui-disabled) .MuiListItemAvatar-root:hover + .MuiListItemText-root > div > div,
          &:not(.Mui-disabled) .MuiListItemText-root > div > div:hover`]: {
            cursor: "pointer",
            backgroundColor: "rgba(0, 0, 0, 0.04)"
        }
    },
    skeleton: {
        border: "1px solid " + theme.palette.borders.line.light,
        borderRadius: 6,
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    },
    avatarContainer: {
        margin: "auto",
        padding: theme.spacing(1),
    },
    guestNameWrapper: {
        width: "100%",
        padding: theme.spacing(2.25, 0, 1.5, 0),
    },
    guestNameText: {
        ...theme.typography.infoMessage,
    },
    emptyGuestText: {
        ...theme.typography.infoMessage,
        color: theme.palette.green
    },
    avatarFont: {
        fontWeight: 400,
        fontSize: `${theme.spacing(2.25)}px`,
        lineHeight: `${theme.spacing(2.25)}px`,
    },
    avatarMemberGuest: {
        backgroundColor: theme.palette.secondary["A700"]
    },
    avatarMemberGuestFont: {
        color: theme.palette.text.black
    },
    avatarEmpty: {
        backgroundColor: theme.palette.primary[200],
        "& > i": {
            color: theme.palette.primary[900]
        },
    },
    avatarSimpleGuest: {
        backgroundColor: theme.palette.secondary[700]
    },
    avatarSimpleGuestFont: {
        color: theme.palette.text.white
    },
    actionsContainer: {
        display: "flex",
        alignItems: "center",
        gap: `${theme.spacing(2)}px`
    },
    textMediumColor: {
        color: theme.palette.text.medium
    },
    fixedHeight: {
        height: "54px"
    }
}));

const ArrivalCardGuestManagementSlot = ({
    guest,
    loading,
    onRemoveGuest,
    onAddGuest,
    selectionMode,
    disabled,
    missingFields,
    isEditable,
    isLocked,
    isClickable,
    onGuestDialogOpen,
    hideActions,
    actionLoading,
    actionDisabled,
}) => {
    const classes = useStyles();
    const {i18n} = useI18N();

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const id = open ? "locked-popover" : undefined;

    const handleGuest = () => {
        if (!guest) {
            if (onAddGuest) {
                onAddGuest(guest);
            }
        } else {
            if (onRemoveGuest) {
                onRemoveGuest();
            }
        }
    };

    const handleSelectGuest = () => {
        if (onAddGuest) {
            onAddGuest(guest);
        }
    };

    const handleGuestClick = () => {
        if (guest && !selectionMode) {
            onGuestDialogOpen(guest);
        }
    };

    const renderAddress = () => {
        const {street, city} = guest?.address || {};
        return `${street || ""}${street && city ? ", " : ""}${city || ""}`;
    };

    return (
        !loading ? (
            <ListItem
                button={isClickable}
                onClick={() => {
                    if (isClickable) {
                        if (!guest) {
                            onAddGuest(guest);
                        } else {
                            handleSelectGuest();
                        }
                    }
                }}
                className={clsx(
                    classes.root,
                    {[classes.active]: !disabled && !guest},
                    {[classes.listItemGuest]: !selectionMode && guest},
                )}
                disabled={disabled}
            >
                <ListItemAvatar
                    className={clsx(classes.avatarContainer, {[classes.fixedHeight]: !selectionMode && guest})}
                    {...(!disabled && {onClick: handleGuestClick})}
                >
                    <Avatar
                        className={
                            guest
                                ? guest?.member_id ? classes.avatarMemberGuest : classes.avatarSimpleGuest
                                : classes.avatarEmpty
                        }
                    >
                        {guest ? (
                            <Typography
                                className={clsx(classes.avatarFont, guest?.member_id
                                    ? classes.avatarMemberGuestFont
                                    : classes.avatarSimpleGuestFont
                                )}
                            >
                                {guest?.first_name[0] + guest?.last_name[0]}
                            </Typography>
                        ) : (
                            <StreamlineIcon >
                                <SingleMan />
                            </StreamlineIcon>
                        )}
                    </Avatar>
                </ListItemAvatar>

                <ListItemText
                    disableTypography
                    primary={
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            py={1}
                        >
                            <Box
                                className={clsx(classes.guestNameWrapper, {[classes.fixedHeight]: !selectionMode && guest})}
                                {...(!disabled && {onClick: handleGuestClick})}
                            >
                                <Typography
                                    className={!selectionMode
                                        ? guest ? classes.guestNameText : classes.emptyGuestText
                                        : undefined
                                    }
                                    variant={selectionMode ? "subtitle2" : undefined}
                                >
                                    {guest
                                        ? guest?.first_name + " " + guest?.last_name
                                        : selectionMode
                                            ? i18n("view.reservations.prepare.guest_management.create_new_co_traveller")
                                            : i18n("view.reservations.prepare.guest_management.add_guest")
                                    }
                                </Typography>

                                {selectionMode &&
                                    <Box mt={0.5}>
                                        <Typography
                                            variant="caption"
                                            className={classes.textMediumColor}
                                        >
                                            {renderAddress()}
                                        </Typography>
                                    </Box>
                                }
                            </Box>

                            {isEditable &&
                                <>
                                    {selectionMode ? (
                                        // Selection mode
                                        <Box className={classes.actionsContainer} >
                                            {missingFields &&
                                                <StreamlineIcon>
                                                    <InformationCircle />
                                                </StreamlineIcon>
                                            }

                                            {!hideActions &&
                                                <Box>
                                                    {guest ? (
                                                        <CustomButton
                                                            size={"small"}
                                                            variant={"contained"}
                                                            onClick={handleSelectGuest}
                                                            disabled={actionDisabled && !actionLoading}
                                                            loading={actionLoading}
                                                        >
                                                            {i18n("common.label.select")}
                                                        </CustomButton>
                                                    ) : (
                                                        <CustomIconButton onClick={() => onAddGuest()}>
                                                            <StreamlineIcon>
                                                                <AddCircle />
                                                            </StreamlineIcon>
                                                        </CustomIconButton>
                                                    )}
                                                </Box>
                                            }
                                        </Box>
                                    ) : (
                                        !isLocked ? (
                                            <CustomIconButton
                                                {...(!disabled && {onClick: handleGuest})}
                                            >
                                                <StreamlineIcon>
                                                    {!guest ? <AddCircle /> : <SubtractCircle />}
                                                </StreamlineIcon>
                                            </CustomIconButton>
                                        ) : (
                                            <>
                                                <IconButton
                                                    aria-describedby={id}
                                                    onClick={(event) => setAnchorEl(event.currentTarget)}
                                                >
                                                    <StreamlineIcon>
                                                        <InformationCircle />
                                                    </StreamlineIcon>
                                                </IconButton>
                                                <Popover
                                                    id={id}
                                                    open={open}
                                                    anchorEl={anchorEl}
                                                    onClose={() => setAnchorEl()}
                                                    anchorOrigin={{
                                                        vertical: "bottom",
                                                        horizontal: "center",
                                                    }}
                                                    transformOrigin={{
                                                        vertical: "top",
                                                        horizontal: "center",
                                                    }}
                                                >
                                                    <Box
                                                        px={3}
                                                        py={2}
                                                    >
                                                        <Typography variant={"body2"}>
                                                            {i18n("view.reservations.prepare.form.guest_added_by_staff_alert")}
                                                        </Typography>
                                                    </Box>
                                                </Popover>
                                            </>
                                        )
                                    )}
                                </>
                            }
                        </Box>
                    }
                />

            </ListItem >
        ) : (
            <Skeleton
                animation="wave"
                variant="rect"
                height={72}
                className={classes.skeleton}
            >
                {i18n("view.reservations.prepare.guest_management.processing_msg")}
            </Skeleton>
        )
    );
};

ArrivalCardGuestManagementSlot.propTypes = {
    guest: PropTypes.any,
    loading: PropTypes.bool,
    onRemoveGuest: PropTypes.func,
    onAddGuest: PropTypes.func,
    selectionMode: PropTypes.bool,
    disabled: PropTypes.bool,
    missingFields: PropTypes.bool,
    isEditable: PropTypes.bool,
    isLocked: PropTypes.bool,
    isClickable: PropTypes.bool,
    hideActions: PropTypes.bool,
    onGuestDialogOpen: PropTypes.func,
    actionLoading: PropTypes.bool,
    actionDisabled: PropTypes.bool,
};

export default ArrivalCardGuestManagementSlot;
